import { eachMonthOfInterval, getMonth } from "date-fns";
import { HighlightCardElement } from "@/components/HighlightCard/HighlightCard";
import { WPClient } from "@/wp/client/WPClient";

const MONTH_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

// Recurring events ignore their date fields. Weekly events are always shown;
// monthly/annual ones only in periods overlapping one of their `event_months`
// (no months = all year round).
const isRecurringEventInRange = (
  repetition: string | undefined,
  eventMonths: string[] | undefined,
  startDate: Date,
  endDate: Date,
) => {
  if (repetition === "weekly" || !eventMonths?.length || startDate > endDate) {
    return true;
  }

  const monthsInRange = eachMonthOfInterval({
    start: startDate,
    end: endDate,
  }).map((month) => MONTH_KEYS[getMonth(month)]);

  return monthsInRange.some((month) => eventMonths.includes(month));
};

interface FetchPostsProps {
  locale: string | undefined;
  defaultLocale: string | undefined;
  startDate?: Date;
  endDate?: Date;
  excludeIds?: number[];
  isRecurringEvent?: boolean;
}

export const fetchEvents = async ({
  startDate,
  endDate,
  locale,
  defaultLocale,
  excludeIds,
  isRecurringEvent,
}: FetchPostsProps) => {
  const client = new WPClient({ locale, defaultLocale });

  const data = await client.getEvents({
    fields: ["id", "link", "acf.header_events"],
    startDate,
    endDate,
    excludeIds,
    isRecurringEvent,
  });
  const posts = (data?.posts ?? []).filter((post) => {
    const header = post?.acf?.header_events;

    return !(
      header?.is_recurring_event &&
      startDate &&
      endDate &&
      !isRecurringEventInRange(
        header.repetition,
        header.event_months,
        startDate,
        endDate,
      )
    );
  });
  const hiddenPostsCount = (data?.posts?.length ?? 0) - posts.length;
  const fetchedEvents =
    posts.map((post) => {
      const header = post?.acf?.header_events;
      const effectiveStartDate =
        header.status === "rescheduled" && header.new_date
          ? header.new_date
          : header?.date;
      const effectiveEndDate =
        header.status === "rescheduled" && header.new_end_date
          ? header.new_end_date
          : header?.end_date;
      const effectiveStartTime =
        header.status === "rescheduled" && header.new_start_time
          ? header.new_start_time
          : header?.start_time;
      const effectiveEndTime =
        header.status === "rescheduled" && header.new_end_time
          ? header.new_end_time
          : header?.end_time;
      return {
        // id: post.id,
        date: effectiveStartDate || "",
        end_date: effectiveEndDate || "",
        start_time: effectiveStartTime,
        end_time: effectiveEndTime,
        title: header?.title,
        excerpt: header?.excerpt,
        subline: "",
        link: post.link,
        image: header.image,
        text_bubble: header?.is_recurring_event ? header.text_bubble : "",
      } as HighlightCardElement;
    }) ?? [];

  return {
    posts: fetchedEvents,
    totalPosts: data.totalPosts - hiddenPostsCount,
    extraData: {
      itemsAfter: data.events_after,
      itemsBefore: data.events_before,
    },
  };
};
